import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Skeleton, Tooltip, Typography, Zoom } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import parse, {
  attributesToProps,
  HTMLReactParserOptions,
  Element,
  domToReact,
  DOMNode,
} from 'html-react-parser';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { fetchExploreTopic, fetchKnowledgeHubPage, fetchPopularDocuments, recordDocumentView } from 'services/confluenceService';
import { fetchAttachmentObjectUrl } from 'services/fetchService';
import { KBPageContent } from 'services/types';
import { useAppDispatch, useAppSelector } from 'store';
import { setCurrentTopic, setPopularDocuments, setPopularLoading, setTopicDetail } from 'store/topicDetailSlice';
import TopicDetailSidebar from 'shared-compoents/topic-detail-sidebar';
import { AboutTopicImage, AboutTopicTitle } from 'pages/topic-detail/styles';
import { sanitizeKnowledgeHubHtml } from './contentSanitizer';
import { extractCodeNode } from './codeBlockContent';
import {
  ContentBody,
  CodeBlockWrapper,
  CopyButton,
  ErrorWrapper,
  BackToTop,
} from './styles';
import {
  ActionButton,
  ActionGroup,
  Article,
  BackButton,
  ChipRow,
  DocumentIcon,
  DocumentLayout,
  DocumentPage,
  DocumentTitle,
  HeaderRow,
  MetadataList,
  RailPanel,
  RailPanelBody,
  SideRail,
  Summary,
  TitleGroup,
  TopicChip,
} from './documentViewStyles';

function isTag(node: DOMNode): node is Element {
  return node.type === 'tag' || node.type === 'script' || node.type === 'style';
}

function CodeBlock({ code, language }: Readonly<{ code: string; language: string }>) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [code]);

  return (
    <CodeBlockWrapper>
      <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="left">
        <CopyButton size="small" onClick={handleCopy} aria-label="Copy code">
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </CopyButton>
      </Tooltip>
      <SyntaxHighlighter language={language} style={oneDark} wrapLongLines>
        {code}
      </SyntaxHighlighter>
    </CodeBlockWrapper>
  );
}

const TRUSTED_EMBED_HOSTS = [
  'lucid.app',
  'lucidchart.com',
  'lucid.co',
  'youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  'vimeo.com',
  'player.vimeo.com',
  'drawio.com',
  'app.diagrams.net',
  'viewer.diagrams.net',
  'docs.google.com',
  'drive.google.com',
] as const;

function isTrustedEmbedUrl(src: string): boolean {
  try {
    const host = new URL(src).hostname.toLowerCase().replace(/^www\./, '');
    return TRUSTED_EMBED_HOSTS.some(
      (trustedHost) => host === trustedHost || host.endsWith(`.${trustedHost}`),
    );
  } catch {
    return false;
  }
}

function isRemoteUrl(src: string): boolean {
  return /^(https?:|data:|blob:)/i.test(src);
}

function isAttachmentPath(href: string): boolean {
  return !isRemoteUrl(href) && /(?:^|\/)knowledge-hub\/attachments\//.test(href);
}

function useResolvedAttachmentUrl(src: string): { resolved: string | null; failed: boolean } {
  const [resolved, setResolved] = useState<string | null>(isRemoteUrl(src) ? src : null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setFailed(false);
    if (isRemoteUrl(src)) {
      setResolved(src);
      return () => {
        active = false;
      };
    }
    setResolved(null);
    const normalizedSrc = src.startsWith('/documentation/')
      ? src.replace(/^\/documentation\//, '')
      : src;
    fetchAttachmentObjectUrl(normalizedSrc)
      .then((url) => {
        if (active) setResolved(url);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [src]);

  return { resolved, failed };
}

type KbImageProps = Readonly<{
  src: string;
  alt?: string;
  width?: string;
  height?: string;
  className?: string;
  title?: string;
}>;

function KbImage({ src, alt, width, height, className, title }: KbImageProps) {
  const { resolved, failed } = useResolvedAttachmentUrl(src);

  if (failed) {
    return <span className="kb-missing-attachment">[image unavailable]</span>;
  }
  if (!resolved) {
    return <span className="kb-image-loading" aria-busy="true" />;
  }
  return (
    <img src={resolved} alt={alt ?? ''} width={width} height={height}
      className={className} title={title} loading="lazy" />
  );
}

type KbMediaProps = {
  readonly kind: 'video' | 'audio';
  readonly src: string;
};

function KbMedia({ kind, src }: KbMediaProps) {
  const { resolved, failed } = useResolvedAttachmentUrl(src);

  if (failed) {
    return <span className="kb-missing-attachment">[{kind} unavailable]</span>;
  }
  if (!resolved) {
    return <span className="kb-media-loading" aria-busy="true" />;
  }
  if (kind === 'video') {
    return (
      <video src={resolved} controls preload="metadata">
        <track kind="captions" />
      </video>
    );
  }
  return (
    <audio src={resolved} controls preload="metadata">
      <track kind="captions" />
    </audio>
  );
}

function KbAttachmentLink({
  href,
  title,
  children,
}: Readonly<{ href: string; title?: string; children: ReactNode }>) {
  const { resolved, failed } = useResolvedAttachmentUrl(href);

  if (failed) {
    return <span className="kb-missing-attachment">{children} [unavailable]</span>;
  }
  return (
    <a
      href={resolved ?? undefined}
      title={title}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={!resolved}
      onClick={resolved ? undefined : (event) => event.preventDefault()}
    >
      {children}
    </a>
  );
}

const parseOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (!isTag(domNode)) {
      return;
    }

    const element = domNode;
    if (element.name === 'pre') {
      const { code, language } = extractCodeNode(element);
      if (code) {
        return <CodeBlock code={code} language={language} />;
      }
    }
    // KB attachment images are stored as backend-relative paths and must be
    // fetched with the bearer token (an <img> tag cannot send headers itself).
    if (element.name === 'img') {
      const src = element.attribs?.src ?? '';
      if (src) {
        return <KbImage src={src} alt={element.attribs.alt} width={element.attribs.width}
          height={element.attribs.height} className={element.attribs.class} title={element.attribs.title} />;
      }
    }
    if (element.name === 'video' || element.name === 'audio') {
      const src = element.attribs?.src ?? '';
      if (src) {
        return <KbMedia kind={element.name} src={src} />;
      }
    }
    if (element.name === 'table') {
      return (
        <div className="kb-table-scroll">
          <table {...attributesToProps(element.attribs)}>
            {domToReact(element.children as DOMNode[], parseOptions)}
          </table>
        </div>
      );
    }
    if (element.name === 'iframe') {
      const src = element.attribs?.src ?? '';
      if (!isTrustedEmbedUrl(src)) {
        return <span className="kb-unsupported-macro">[Embedded content unavailable]</span>;
      }
    }
    if (
      element.name === 'a' &&
      (element.attribs?.['data-attachment'] === 'true' || isAttachmentPath(element.attribs?.href ?? ''))
    ) {
      const href = element.attribs?.href ?? '';
      if (href) {
        return (
          <KbAttachmentLink href={href} title={element.attribs.title}>
            {domToReact(element.children as DOMNode[], parseOptions)}
          </KbAttachmentLink>
        );
      }
    }
    // Open external links in a new tab safely.
    if (
      element.name === 'a' &&
      element.attribs?.href?.startsWith('http')
    ) {
      return (
        <a href={element.attribs.href} target="_blank" rel="noopener noreferrer">
          {domToReact(element.children as DOMNode[], parseOptions)}
        </a>
      );
    }
  },
};

function formatDate(value?: string | null): string {
  if (!value) return 'Not available';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

function formatTopicName(topic: string): string {
  return topic.replace(/^enclave-kb_/, '');
}

function getTopicId(topic: string): string | null {
  const prefix = 'enclave-kb_';
  return topic.startsWith(prefix) ? topic.slice(prefix.length) : null;
}

function Documentation() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.userData.access_token);
  const originatingTopicId =
    (location.state as { topicId?: string } | null)?.topicId ?? searchParams.get('from') ?? undefined;
  const currentTopicId = useAppSelector((state) => state.topicDetail.currentTopicId);
  const resolvedTopicId = originatingTopicId ?? currentTopicId;
  const currentTopic = useAppSelector((state) =>
    resolvedTopicId ? state.topicDetail.topics[resolvedTopicId] : null
  );
  const popularDocuments = useAppSelector((state) => state.topicDetail.popularDocuments);
  const popularLoading = useAppSelector((state) => state.topicDetail.popularLoading);
  const [page, setPage] = useState<KBPageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTop, setShowTop] = useState(false);
  const [compactHeader, setCompactHeader] = useState(false);
  const sanitizedContent = useMemo(
    () => sanitizeKnowledgeHubHtml(page?.content ?? ''),
    [page?.content],
  );

  const loadPage = useCallback(() => {
    if (!pageId || !accessToken) return;
    setLoading(true);
    setError(null);
    fetchKnowledgeHubPage(pageId)
      .then((data) => {
        setPage(data);
        void recordDocumentView(pageId);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load page.'))
      .finally(() => setLoading(false));
  }, [pageId, accessToken]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (!accessToken || !originatingTopicId) return;
    dispatch(setCurrentTopic(originatingTopicId));
    if (!currentTopic) {
      fetchExploreTopic(originatingTopicId)
        .then((data) => dispatch(setTopicDetail(data)))
        .catch(() => {});
    }
  }, [originatingTopicId, accessToken, currentTopic, dispatch]);

  useEffect(() => {
    if (!accessToken) return;
    dispatch(setPopularLoading(true));
    fetchPopularDocuments()
      .then((data) => dispatch(setPopularDocuments(data.documents)))
      .catch(() => dispatch(setPopularLoading(false)));
  }, [accessToken, dispatch]);

  useEffect(() => {
    // Hysteresis: separate on/off thresholds so the shrinking header can't
    // oscillate the scroll position near a single boundary.
    const onScroll = () => {
      const scrolled = window.scrollY;
      setShowTop(scrolled > 400);
      setCompactHeader((current) => {
        if (!current && scrolled > 80) return true;
        if (current && scrolled < 24) return false;
        return current;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (loading) {
    return (
      <DocumentPage>
        <DocumentLayout>
          <Article>
            <HeaderRow>
              <TitleGroup>
                <Skeleton variant="rounded" width={64} height={64} />
                <div>
                  <Skeleton variant="text" width={320} height={34} />
                  <Skeleton variant="text" width={140} height={22} />
                </div>
              </TitleGroup>
            </HeaderRow>
            <Skeleton variant="text" width="90%" height={20} sx={{ mt: 2, mb: 2 }} />
            <Skeleton
              variant="rounded"
              width="100%"
              height="calc(100vh - 260px)"
              sx={{ flexGrow: 1 }}
            />
          </Article>

          <SideRail>
            <Skeleton variant="rounded" width="100%" height={200} sx={{ flexShrink: 0 }} />
            <Skeleton variant="rounded" width="100%" height="100%" sx={{ flexGrow: 1 }} />
          </SideRail>
        </DocumentLayout>
      </DocumentPage>
    );
  }

  if (error) {
    return (
      <ErrorWrapper>
        <ErrorOutlineIcon sx={{ fontSize: 48, color: '#EB0A1E' }} />
        <Typography variant="h6" sx={{ color: '#101828' }}>
          Unable to load this page
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 420 }}>
          {error}
        </Typography>
        <Button
          onClick={loadPage}
          startIcon={<RefreshIcon />}
          variant="outlined"
          sx={{ mt: 1, borderColor: '#EB0A1E', color: '#EB0A1E' }}
        >
          Retry
        </Button>
      </ErrorWrapper>
    );
  }

  if (!page) return null;

  const currentDocument = currentTopic?.documents.find((document) => document.id === page.page_id);
  const topics = currentDocument?.tags.length ? currentDocument.tags : page.labels ?? [];
  const updatedAt = formatDate(page.source_updated_at ?? page.cached_at);
  const documentType = currentDocument?.type || 'Document';
  const sidebarPopularDocuments = popularDocuments.filter((document) => document.id !== page.page_id);
  const originatingTopic = originatingTopicId ? currentTopic : null;

  const handleShare = async () => {
    const shareData = { title: page.title, url: window.location.href };
    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }
    await navigator.clipboard.writeText(window.location.href);
  };

  return (
    <DocumentPage>
      {originatingTopic && (
        <BackButton
          type="button"
          onClick={() => navigate(`/explore-topics/${originatingTopic.id}`)}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back to {originatingTopic.title}
        </BackButton>
      )}

      <DocumentLayout>
        <Article>
          <HeaderRow compact={compactHeader}>
            <TitleGroup compact={compactHeader}>
              <DocumentIcon compact={compactHeader}>
                <DescriptionOutlinedIcon />
              </DocumentIcon>
              <div>
                <DocumentTitle compact={compactHeader}>{page.title}</DocumentTitle>
                <ChipRow compact={compactHeader}>
                  <TopicChip>{documentType}</TopicChip>
                  {topics.slice(0, 3).map((topic) => {
                    const topicId = getTopicId(topic);
                    return (
                      <TopicChip
                        key={topic}
                        as={topicId ? 'button' : 'span'}
                        onClick={topicId ? () => navigate(`/explore-topics/${topicId}`) : undefined}
                      >
                        {formatTopicName(topic)}
                      </TopicChip>
                    );
                  })}
                </ChipRow>
              </div>
            </TitleGroup>
            <ActionGroup>
              {/* <ActionButton type="button" onClick={() => setBookmarked((value) => !value)}>
                {bookmarked ? <BookmarkIcon sx={{ fontSize: 17 }} /> : <BookmarkBorderIcon sx={{ fontSize: 17 }} />}
                {bookmarked ? 'Saved' : 'Bookmark'}
              </ActionButton> */}
              <ActionButton type="button" onClick={() => void handleShare()}>
                <ShareOutlinedIcon sx={{ fontSize: 17 }} />
                Share
              </ActionButton>
              {/* <MoreButton aria-label="More document actions"><MoreHorizIcon /></MoreButton> */}
            </ActionGroup>
          </HeaderRow>

          {currentDocument?.description && <Summary>{currentDocument.description}</Summary>}
          {/* <InlineMeta>
            <InlineMetaItem>Last updated: {updatedAt}</InlineMetaItem>
            <InlineMetaItem>Version: {page.version ?? 0}</InlineMetaItem>
            {page.space_key && <InlineMetaItem>Space: {page.space_key}</InlineMetaItem>}
          </InlineMeta> */}

          {/* {page.warning && (
            <ContentWarning>
              <WarningAmberIcon sx={{ fontSize: 18, mt: '1px' }} />
              <span>{page.warning}</span>
            </ContentWarning>
          )} */}
          <ContentBody className="markdown-body">{parse(sanitizedContent, parseOptions)}</ContentBody>
        </Article>

        <SideRail>
          <RailPanel>
            <RailPanelBody>
              
              <AboutTopicTitle>About this document <AboutTopicImage src="/assets/knowledge-hub.svg" alt="" /></AboutTopicTitle>
              <MetadataList>
                <dt>Type</dt><dd>{documentType}</dd>
                <dt>Last updated</dt><dd>{updatedAt}</dd>
                <dt>Version</dt><dd>{page.version ?? 0}</dd>
                {topics.length > 0 && (
                  <><dt>Topics</dt><dd><ChipRow>{topics.slice(0, 3).map((topic) => {
                    const topicId = getTopicId(topic);
                    return (
                      <TopicChip
                        key={topic}
                        as={topicId ? 'button' : 'span'}
                        onClick={topicId ? () => navigate(`/explore-topics/${topicId}`) : undefined}
                      >
                        {formatTopicName(topic)}
                      </TopicChip>
                    );
                  })}</ChipRow></dd></>
                )}
              </MetadataList>
            </RailPanelBody>
          </RailPanel>

          <TopicDetailSidebar
            popularDocuments={sidebarPopularDocuments}
            loading={popularLoading}
            onSelectDocument={(documentId) =>
              navigate(
                originatingTopicId
                  ? `/documentation/${documentId}?from=${originatingTopicId}`
                  : `/documentation/${documentId}`
              )
            }
          />
        </SideRail>
      </DocumentLayout>

      <Zoom in={showTop}>
        <BackToTop aria-label="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <KeyboardArrowUpIcon />
        </BackToTop>
      </Zoom>
    </DocumentPage>
  );
}

export default Documentation;
